import { Avatar, Box, Button, IconButton, Link, Skeleton, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { colors } from "../../../theme/palette";
import { ChatIcon, FollowIcon, VerificationIcon } from "../../../assets/icons/MUI/NetworkIcons";

import { followEntity, followUser } from "../../../api/network";
import { useInvalidateMyQuery } from "../../../hooks/useInvalidateMyQuery";
import { UserInfo } from "../../../types";
import { useProfileData } from "../../../hooks/useProfileData";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import NewChatPopUp from "../../../shared/NewChatPopUp";
import { DeleteUserIcon } from "../../../assets/icons/MUI/ExploreIcons";

const UserCard = ({ data, handleRemoveUser }: { data: UserInfo; handleRemoveUser: (id: number | null) => void }) => {
  const invalidateNetworkFollowersList = useInvalidateMyQuery("NetworkList");
  const [openCreateMessage, setOpenCreateMessage] = useState<boolean>(false);
  const navigate = useNavigate();

  const {
    id,
    fullName,
    firstTwoChr,
    countryMedia,
    isVerified,
    media,
    role,
    isFollowing,
    profileLink,
    mutualFollowersCount,
    playerPositionShort,
    friendsCount,
    entityId,
  } = useDataFormatter(data);
  const [isFollowingState, setIsFollowingState] = useState<boolean>(isFollowing || false);
  const IS_ENTITY = data.entity;

  const { data: ownProfileData } = useProfileData();
  const { entityId: entityIdFromCache } = ownProfileData;

  const isOwnProfile = entityId == entityIdFromCache && entityIdFromCache != null;

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "14px",
        padding: "12px",
        position: "relative",
        borderRadius: "4px",
        background: colors.White,
        boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
        height: "250px",
        width: "170px",
      }}
    >
      <IconButton
        onClick={() => handleRemoveUser(id)}
        sx={{
          position: "absolute",
          top: "10px",
          right: "10px",
        }}
      >
        <DeleteUserIcon />
      </IconButton>
      {isVerified && (
        <VerificationIcon
          sx={{
            position: "absolute",
            top: "72px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: "10",
          }}
        />
      )}
      <Avatar
        sx={{
          width: "72px",
          height: "72px",
          margin: "0 auto 9px",
        }}
        src={media}
      >
        {firstTwoChr}
      </Avatar>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "3px",
            width: "100%",
            justifyContent: "center",
            marginBottom: "5.5px",
          }}
        >
          <Link
            sx={{
              textDecoration: "none",
              color: colors.dark1000,
              textAlign: "center",
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "normal",
              maxWidth: "80%",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={() => navigate(profileLink)}
          >
            {fullName}
          </Link>
          <img src={countryMedia} height={"9px"} width={"12px"} />
        </Box>
        <Typography
          component="span"
          variant="body12Regular"
          sx={{
            color: colors.Dark1000,
            textAlign: "center",
          }}
        >
          {role}
          {playerPositionShort ? ` . (${playerPositionShort})` : ""}
        </Typography>

        <Typography
          component="span"
          variant="body12Regular"
          sx={{
            color: colors.Dark600,
            textAlign: "center",
          }}
        >
          {friendsCount ? `${friendsCount} ${t("components.networkController.userCard.friends")}` : ""}
        </Typography>

        {isOwnProfile ? (
          <></>
        ) : (
          <Typography
            component="span"
            variant="body12Regular"
            sx={{
              color: colors.Dark600,
              textAlign: "center",
            }}
          >
            {`${mutualFollowersCount} ${t("components.networkController.userCard.commonFollowers")}`}
          </Typography>
        )}
      </Box>

      {isOwnProfile ? (
        <></>
      ) : isFollowingState ? (
        <>
          <Button
            variant="secondary-sm"
            fullWidth
            startIcon={currentLanguage === "en" && <ChatIcon />}
            endIcon={currentLanguage === "ar" && <ChatIcon />}
            sx={{
              marginTop: "auto",
            }}
            onClick={() => setOpenCreateMessage(true)}
          >
            {t("components.networkController.userCard.chat")}
          </Button>
          {openCreateMessage && (
            <NewChatPopUp
              open={openCreateMessage}
              handleClose={() => setOpenCreateMessage(false)}
              receiverInfo={data}
            />
          )}
        </>
      ) : (
        <Button
          variant="outlined-sm"
          fullWidth
          startIcon={currentLanguage === "en" && <FollowIcon />}
          endIcon={currentLanguage === "ar" && <FollowIcon />}
          onClick={async () => {
            let success;
            if (IS_ENTITY) {
              success = await followEntity(data.entity!.id);
            } else {
              success = await followUser(data.id);
            }
            if (success) {
              invalidateNetworkFollowersList();
              setIsFollowingState(true);
            }
          }}
          sx={{
            marginTop: "auto",
          }}
        >
          {t("components.networkController.userCard.follow")}
        </Button>
      )}
    </Box>
  );
};

export default UserCard;

export const UserCardSkeleton = () => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "14px",
        padding: "12px",
        position: "relative",
        borderRadius: "4px",
        background: colors.White,
        boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
        height: "250px",
        width: "170px",
      }}
    >
      {/* Checkbox Placeholder */}
      <Skeleton variant="rounded" width={24} height={24} sx={{ position: "absolute", left: "7px", top: "7px" }} />

      {/* Avatar Skeleton */}
      <Skeleton variant="circular" width={72} height={72} sx={{ margin: "0 auto 9px" }} />

      {/* Name and Country Flag Skeleton */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "6px",
            width: "100%",
            justifyContent: "center",
            marginBottom: "5.5px",
          }}
        >
          <Skeleton variant="text" width="60%" height={18} />
          <Skeleton variant="text" width={12} height={18} />
        </Box>

        {/* Role and Player Position Skeleton */}
        <Typography component="span" variant="body12Regular">
          <Skeleton variant="text" width="80px" />
        </Typography>

        {/* Friends Count Skeleton */}
        <Typography component="span" variant="body12Regular" sx={{ color: colors.Dark600, textAlign: "center" }}>
          <Skeleton variant="text" width="90px" />
        </Typography>

        {/* Common Followers Count Skeleton */}
        <Typography component="span" variant="body12Regular" sx={{ color: colors.Dark600, textAlign: "center" }}>
          <Skeleton variant="text" width="120px" />
        </Typography>
      </Box>

      {/* Button Skeleton */}
      <Skeleton variant="rounded" height={36} width="100%" sx={{ marginTop: "auto", borderRadius: "50px" }} />
    </Box>
  );
};
